/* The paper lens (snippets/xp-paper-lens.liquid; group paper-shop, docs/build/pages/paper-shop-v2.md "Polish round 1"
   and "Polish round 2").
   Deferred; runs after xp-motion.js and uses its engine (XP.motion.register / watch). Writes --lp (0 → 1: the loupe
   over ordinary paper → the XNote notebook page → the XNote printable page) on the figure and [data-lens-at] (0, 1, 2:
   the sheet under the loupe) on the nearest [data-lens-scope], which lights that column of the xp-paper copy, and
   --lm (0 over a sheet, 1 mid-travel: the loupe lifts and its magnified view fades while it moves).
   - Track mode (data-paper-lens="track", ≥ 768 px wide and ≥ 600 px tall): progress through the caller's sticky track [data-lens-track],
     eased through a held beat on each sheet (20 % dwell each), so every comparison is read while the stage is pinned.
   - Free mode (phones on Printables, and the compact lens at every width; Polish round 2 re-run, critic r2 #5: a
     timer that started at 35 % visibility let a normal scroll pass before the dots ever showed): the stage's own pass
     through the viewport drives the loupe, 0 when its top reaches 85 % of the viewport height and 1 at 25 %, with the
     same held beats, so scrolling itself sweeps it across the three sheets (both ways) without pinning. Idle
     fallback: once scrolling stops for 0.9 s with the stage ≥ 35 % in view, the timeline carries on from there (the
     whole run is 6 s), paused off screen or in a hidden tab; the next scroll takes over again and never moves the
     loupe back behind where the idle play left it.
     A tap on a sheet or on a caption, or a swipe on phones, moves the lens there (and stops both).
     The captions never depend on it: all three are always visible; the lit one only changes colour.
   Reduced motion, no JS or no engine: nothing runs; the CSS default (--lp: 1, three sheets) is the final state. */
(() => {
  'use strict';
  const d = document, w = window;
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const DWELL = 0.2, MOVE = (1 - 3 * DWELL) / 2, T = 6000;
  const held = (p) => {
    if (p < DWELL) return 0;
    if (p < DWELL + MOVE) return 0.5 * ease((p - DWELL) / MOVE);
    if (p < 2 * DWELL + MOVE) return 0.5;
    if (p < 2 * DWELL + 2 * MOVE) return 0.5 + 0.5 * ease((p - 2 * DWELL - MOVE) / MOVE);
    return 1;
  };
  const phoneQ = w.matchMedia('(max-width: 767px)');
  // Must match the CSS that makes the Printables track tall (assets/xp-paper-page.css).
  const trackQ = w.matchMedia('(min-width: 768px) and (min-height: 600px)');

  function setup(fig, M) {
    const stage = fig.querySelector('.xp-lens__stage');
    if (!stage) return;
    const scope = fig.closest('[data-lens-scope]') || fig;
    const track = fig.getAttribute('data-paper-lens') === 'track' ? fig.closest('[data-lens-track]') : null;
    const scrollMode = () => Boolean(track) && trackQ.matches;
    let lp = -1, at = '';
    const set = (v) => {
      v = clamp(v);
      if (Math.abs(v - lp) < 1e-4) return;
      lp = v;
      fig.style.setProperty('--lp', v.toFixed(4));
      fig.style.setProperty('--lm', Math.sin(Math.PI * (v * 2 - Math.floor(v * 2))).toFixed(3));
      const a = String(Math.round(v * 2));
      if (a !== at) { at = a; scope.setAttribute('data-lens-at', a); }
    };
    fig.classList.add('is-live');
    scope.classList.add('has-live-lens');

    if (track) {
      const sticky = track.querySelector('[data-lens-sticky]') || fig;
      M.register(track, (t, y) => {
        if (scrollMode()) set(held(clamp((y + t.off - t.top) / Math.max(1, t.h - t.sh))));
      }, (t) => {
        t.sh = sticky.offsetHeight;
        sticky.style.setProperty('--lens-sticky-h', t.sh + 'px'); // centres the pinned block (xp-paper-page.css)
        t.off = parseFloat(w.getComputedStyle(sticky).top) || 0;
      });
    }

    // Free mode: scroll progress through the viewport (sp), plus an idle timeline that only ever pushes forward (floor).
    let vh = w.innerHeight, sp = 0, floor = 0, raf = 0, last = 0, idle = 0, visible = false, manual = false;
    w.addEventListener('resize', () => { vh = w.innerHeight; });
    const show = () => set(held(Math.max(sp, floor)));
    const stop = () => { if (raf) w.cancelAnimationFrame(raf); raf = 0; if (idle) w.clearTimeout(idle); idle = 0; };
    const frame = (now) => {
      raf = 0;
      if (scrollMode() || manual || !visible || d.hidden) return;
      floor = clamp(Math.max(floor, sp) + Math.min(100, now - (last || now)) / T);
      last = now;
      show();
      if (floor < 1) raf = w.requestAnimationFrame(frame);
    };
    const arm = () => {
      stop();
      if (scrollMode() || manual || !visible || d.hidden || Math.max(sp, floor) >= 1) return;
      idle = w.setTimeout(() => { idle = 0; last = 0; raf = w.requestAnimationFrame(frame); }, 900);
    };
    M.register(stage, (t, y) => {
      if (scrollMode() || manual) return;
      sp = clamp((0.85 * vh - (t.top - y)) / (0.6 * vh));
      show();
      arm();
    });
    let tw = 0;
    const go = (i) => {
      if (scrollMode()) return;
      manual = true;
      stop();
      if (tw) w.cancelAnimationFrame(tw);
      const from = lp < 0 ? 1 : lp, to = clamp(i / 2), t0 = performance.now(), ms = 520 + 380 * Math.abs(to - from);
      const step = (now) => { const k = clamp((now - t0) / ms); set(from + (to - from) * ease(k)); tw = k < 1 ? w.requestAnimationFrame(step) : 0; };
      tw = w.requestAnimationFrame(step);
    };
    if (!scrollMode()) set(0);
    M.watch(stage, (e) => { visible = e.isIntersecting; if (visible) arm(); else stop(); }, { threshold: 0.35, once: false });
    d.addEventListener('visibilitychange', () => { if (d.hidden) stop(); else arm(); });
    const onMode = () => { stop(); M.refresh(); if (!scrollMode()) arm(); };
    phoneQ.addEventListener('change', onMode);
    trackQ.addEventListener('change', onMode);

    // Tap a sheet (three across: its column; one at a time: the side you tap), tap a column, or swipe on phones.
    let x0 = null, y0 = 0, swiped = false;
    stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; swiped = false; });
    stage.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      x0 = null;
      if (phoneQ.matches && Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) { swiped = true; go(Math.round(lp * 2) + (dx < 0 ? 1 : -1)); }
    });
    stage.addEventListener('click', (e) => {
      if (swiped) { swiped = false; return; }
      const r = stage.getBoundingClientRect(), f = (e.clientX - r.left) / Math.max(1, r.width), cur = Math.round(lp * 2);
      if (phoneQ.matches) go(f < 0.3 ? cur - 1 : f > 0.7 ? cur + 1 : cur + (cur < 2 ? 1 : -2));
      else go(f < 1 / 3 ? 0 : f < 2 / 3 ? 1 : 2);
    });
    scope.querySelectorAll('.xp-paper__col').forEach((col, i) => col.addEventListener('click', (e) => { if (!e.target.closest('a')) go(i); }));
  }

  function init() {
    const M = w.XP && w.XP.motion;
    if (!M || M.reduced || !M.register || !M.watch) return;
    d.querySelectorAll('[data-paper-lens]').forEach((fig) => setup(fig, M));
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init);
  else init();
})();
